import ufeApi from 'services/api/ufeApi';
import UUIDv4 from 'utils/UUID';
import Hashing from 'utils/Hashing';
import authenticationUtils from 'utils/Authentication';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import GenerateTokens from 'services/api/authentication/generateLithiumSsoToken';
import RCPSCookies from 'utils/RCPSCookies';
import { URLS, AUTH_HEADERS } from 'constants/authentication';
import { MFA_MODE } from 'constants/phoneVerification';
import { setAuthOperationInProgress } from 'services/auth/newFlow/authFlow';
import { loadBeyondIdentityUtils } from 'utils/lazyPasskey';
import passkeysUtils from 'utils/passkeys';
import PasskeyService from 'services/PasskeyService';
import Actions from 'Actions';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import PasskeysActionsEN from 'actions/locales/PasskeysActions_en_US';
import PasskeysActionsFR from 'actions/locales/PasskeysActions_fr_CA';
import store from 'store/Store';
import { ERR_PK_LOGIN_ERROR } from 'constants/passkeys';

const { sha256 } = Hashing;
const { isAuthServiceEnabled, getFingerPrint } = authenticationUtils;
const { generateTokens } = GenerateTokens;
const { getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(PasskeysActionsEN, PasskeysActionsFR);
const { isPasskeyEnabled, cleanupPasskeysByEmail } = passkeysUtils;

/**
 * Check if error is a revoked passkey error from BeyondIdentity
 * @param {Object} error - Error object
 * @returns {boolean}
 */
const isRevokedPasskeyError = error => {
    if (error?.message && /state is REVOKED/i.test(error.message)) {
        return true;
    }

    return false;
};

/**
 * Handle passkey error - show error modal and reopen sign-in
 * @param {string} email - User email
 * @param {boolean} shouldCleanup - Whether to cleanup passkeys before showing modal
 * @param {Object|Error} error - The error object (optional, for analytics tracking)
 * @param {boolean} isSignInWithMessaging - Whether to reopen SignInWithMessagingModal (checkout) instead of regular SignInModal
 */
const handlePasskeyError = async (email, shouldCleanup = false, error = null, isSignInWithMessaging = false) => {
    // Clean up all passkeys for this email if requested
    if (shouldCleanup) {
        await cleanupPasskeysByEmail(email);
    }

    const errorCode = error?.errorCode;

    const errorMessage = errorCode === ERR_PK_LOGIN_ERROR ? getText('passkeyLoginError') : getText('passkeyAuthenticationErrorMessage');
    const BeyondIdentityUtils = await loadBeyondIdentityUtils();

    // Track passkey authentication error with Adobe Analytics
    BeyondIdentityUtils.trackPasskeyError(errorMessage, error);

    // Show error modal
    store.dispatch(
        Actions.showInfoModal({
            isOpen: true,
            title: getText('error'),
            message: errorMessage,
            buttonText: getText('ok'),
            showCloseButton: false,
            callback: () => {
                store.dispatch(Actions.showInfoModal({ isOpen: false }));

                const modalAction = isSignInWithMessaging ? Actions.showSignInWithMessagingModal : Actions.showSignInModal;

                store.dispatch(
                    modalAction({
                        isOpen: true,
                        email,
                        extraParams: {
                            skipPasskeyCheck: true
                        }
                    })
                );
            }
        })
    );
};

/**
 * Handle revoked passkey error - cleanup and show error modal
 * @param {string} email - User email
 * @param {Object|Error} error - The error object (optional, for analytics tracking)
 */
const handleRevokedPasskeyError = async (email, error, isSignInWithMessaging = false) => {
    await handlePasskeyError(email, true, error, isSignInWithMessaging);
};

// https://jira.sephora.com/wiki/display/ILLUMINATE/Login+API

function login({ deviceFingerprint, login: log1n, password, options }) {
    const {
        loginForCheckout, isKeepSignedIn, isOrderConfirmation, biAccountInfo, extraParams: allExtraParams
    } = options;
    const { isCheckoutInitAttempt, headerValue, isSignInWithMessaging: isSignInWithMessagingFromExtra, ...extraParams } = allExtraParams || {};

    let body = {
        login: log1n,
        password,
        ...(isKeepSignedIn && deviceFingerprint && { deviceFingerprint })
    };

    if (isOrderConfirmation) {
        body.source = 'orderConfirmation';

        if (biAccountInfo) {
            body.isJoinBi = biAccountInfo.isJoinBi;
            body.biAccount = biAccountInfo.birthday;
            body.subscription = biAccountInfo.subscription;
        }
    } else {
        body.loginForCheckout = loginForCheckout;
        body.isKeepSignedIn = isKeepSignedIn;
    }

    if (Object.keys(extraParams).length) {
        body = {
            ...body,
            ...extraParams
        };
    }

    // Passkey flow: ensure the local device id is not sent to backend
    if (extraParams?.isPasskeyLogin && 'id' in body) {
        delete body.id;
    }

    if (isAuthServiceEnabled()) {
        body.email = body.login;
        delete body.login;

        body.keepSignedIn = body.isKeepSignedIn;
        delete body.isKeepSignedIn;

        const randomNumber = UUIDv4();

        const loginPromise = sha256(randomNumber).then(async function (digest) {
            const token = digest;
            body.randomNumber = token;
            let url = URLS.ATG_LOGIN_URL;
            const headers = { ...AUTH_HEADERS };
            const xCausedHeader = headerValue || location.href;
            headers['X-CAUSED-BY-URL'] = xCausedHeader;

            if (RCPSCookies.isRCPSAuthEnabled()) {
                url = URLS.SDN_LOGIN_URL;
                headers.deviceId = await getFingerPrint();
            }

            const performLoginRequest = async () => {
                return ufeApi
                    .makeRequest(
                        url,
                        {
                            method: 'POST',
                            body: JSON.stringify(body),
                            headers
                        },
                        {
                            // Skip token validation to avoid circular dependency:
                            // makeRequest would check token freshness and trigger auth flow,
                            // which waits for this login call to complete (deadlock).
                            skipTokenCheck: true
                        }
                    )
                    .then(async tokenData => {
                        if (tokenData?.errors) {
                            return Promise.reject(tokenData.errors[0]);
                        }

                        if (tokenData?.responseStatus >= 400) {
                            return Promise.reject(tokenData);
                        }

                        if (Sephora.configurationSettings?.enableStepUpAuthFlowBP2 && tokenData?.mfaEnabled) {
                            const mfaChallengeOptions = {};

                            if (tokenData?.mfaMode === MFA_MODE.PHONE) {
                                mfaChallengeOptions['shouldTriggerPhoneMFA'] = true;
                                mfaChallengeOptions['phoneLast4Digits'] = tokenData?.phoneLast4Digits;
                            }

                            tokenData?.mfaMode === MFA_MODE.EMAIL && (mfaChallengeOptions.shouldTriggerEmailMfa = true);

                            // MFA required then clear flag to unblock background API calls
                            setAuthOperationInProgress(false);

                            return Promise.resolve({
                                mfaEnabled: true,
                                hasIdentity: !!tokenData.hasIdentity,
                                ...mfaChallengeOptions
                            });
                        }

                        if (tokenData?.isStoreBiMember && Sephora.configurationSettings?.isEmailVerificationEnabled) {
                            // Email verification required for BI members, then clear flag to unblock background API calls
                            setAuthOperationInProgress(false);

                            return Promise.resolve({
                                shouldTriggerEmailVerification: true,
                                isStoreBiMember: true
                            });
                        }

                        if (tokenData?.profileWarnings) {
                            Storage.local.setItem(LOCAL_STORAGE.LOGIN_PROFILE_WARNINGS, tokenData?.profileWarnings);

                            if (isCheckoutInitAttempt) {
                                Storage.local.setItem(LOCAL_STORAGE.POSTPONE_LOGIN_PROFILE_WARNINGS, true);
                            }
                        }

                        body.token = tokenData.token;
                        body.randomNumber = randomNumber;

                        const generateTokensResponse = await generateTokens(body, { headerValue: xCausedHeader });

                        // Clear auth operation flag BEFORE returning, so subsequent API calls (like getUserFull) don't wait
                        // The flag must be cleared here (not in .finally()) to avoid deadlock where getUserFull waits for
                        // loginPromise, but loginPromise can't resolve until getUserFull completes
                        setAuthOperationInProgress(false);

                        return {
                            ...generateTokensResponse,
                            hasIdentity: tokenData.hasIdentity
                        };
                    })
                    .catch(error => {
                        // Handle passkey login errors
                        if (extraParams?.isPasskeyLogin) {
                            setAuthOperationInProgress(false);

                            // NOTE: ERR_I_00000 is a general error code used for multiple error scenarios
                            // Waiting for backend to provide a specific error code for account deactivation
                            // TODO: Uncomment and update when backend provides specific error code
                            /*
                            if (error?.errorCode === 'SPECIFIC_ACCOUNT_DEACTIVATED_CODE') {
                                // Clean up passkeys for this email if account is deactivated
                                await cleanupPasskeysByEmail(body.email);
                                store.dispatch(Actions.showSignInModal({ isOpen: false }));
                                store.dispatch(
                                    Actions.showAccountDeactivatedModal({
                                        isOpen: true,
                                        errorMessage: error?.errorMessage || getText('passkeyAccountDeactivatedMessage')
                                    })
                                );

                                const deactivatedError = new Error('Account deactivated');
                                deactivatedError.errorCode = 'ACCOUNT_DEACTIVATED';
                                deactivatedError.passkeyFailed = true;

                                return Promise.reject(deactivatedError);
                            }
                            */

                            // Check if this is a revoked passkey error from backend
                            if (isRevokedPasskeyError(error)) {
                                handleRevokedPasskeyError(body.email, error, isSignInWithMessagingFromExtra);

                                const revokedError = new Error('Passkey has been revoked');
                                revokedError.errorCode = 'PASSKEY_REVOKED';
                                revokedError.passkeyFailed = true;

                                return Promise.reject(revokedError);
                            }

                            // Handle all other passkey errors with the same modal (without cleanup)
                            handlePasskeyError(body.email, false, error, isSignInWithMessagingFromExtra);

                            const passkeyError = new Error('Passkey authentication failed');
                            passkeyError.errorCode = 'PASSKEY_FAILED';
                            passkeyError.passkeyFailed = true;

                            return Promise.reject(passkeyError);
                        }

                        // Re-throw other errors (non-passkey login)
                        // Clear flag on error (non-passkey path)
                        setAuthOperationInProgress(false);
                        throw error;
                    });
            };

            if (!extraParams?.isPasskeyLogin || !extraParams?.passkeyId) {
                return performLoginRequest();
            }

            // Don't attempt passkey login if feature is disabled
            if (!isPasskeyEnabled()) {
                return performLoginRequest();
            }

            // Passkey login flow
            try {
                const BeyondIdentityUtils = await loadBeyondIdentityUtils();
                const credentialId = extraParams.passkeyCredentialId;

                // Use SHA-256 digest as state
                const { authenticateUrl, state } = await PasskeyService.generateAuthenticateUrl(body.randomNumber);

                // Authenticate with SDK for the specific passkey
                const authResult = await BeyondIdentityUtils.authenticate(authenticateUrl, credentialId);

                // Parse redirect URL returned by SDK
                const urlParams = PasskeyService.parseRedirectUrl(authResult.redirectUrl);

                // Build API URL for passkey login (dynamic host via ufeApi)
                const queryParams = new URLSearchParams({
                    code: urlParams.code,
                    state: urlParams.state || state,
                    redirectUrl: PasskeyService.getRedirectUri(),
                    identityId: extraParams.identityId,
                    passkeyId: extraParams.passkeyId
                });

                url = `/gway/v1/dotcom/auth/passkeylogin?${queryParams.toString()}`;
            } catch (error) {
                setAuthOperationInProgress(false);

                // Check if this is a revoked passkey error
                if (isRevokedPasskeyError(error)) {
                    handleRevokedPasskeyError(body.email, error, isSignInWithMessagingFromExtra);

                    const revokedError = new Error('Passkey has been revoked');
                    revokedError.errorCode = 'PASSKEY_REVOKED';
                    revokedError.passkeyFailed = true;

                    return Promise.reject(revokedError);
                }

                // Handle all other passkey errors with the same modal (without cleanup)
                handlePasskeyError(body.email, false, error, isSignInWithMessagingFromExtra);

                const passkeyError = new Error('Passkey authentication failed');
                passkeyError.errorCode = 'PASSKEY_FAILED';
                passkeyError.passkeyFailed = true;

                return Promise.reject(passkeyError);
            }

            return performLoginRequest();
        });

        // Set flag to skip auth flow during login, passing the promise so other calls can wait
        setAuthOperationInProgress(true, loginPromise);

        return loginPromise;
    }

    const url = '/api/auth/login';

    const legacyLoginPromise = ufeApi
        .makeRequest(url, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                'Content-type': 'application/json',
                'x-requested-source': 'web'
            }
        })
        .then(data => {
            // Clear auth operation flag BEFORE checking for errors, so subsequent API calls don't wait
            setAuthOperationInProgress(false);

            return data.errorCode ? Promise.reject(data) : data;
        })
        .catch(error => {
            // Ensure flag is cleared on error too
            setAuthOperationInProgress(false);
            throw error;
        });

    // Set flag to skip auth flow during login, passing the promise so other calls can wait
    setAuthOperationInProgress(true, legacyLoginPromise);

    return legacyLoginPromise;
}

export default { login };
